import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Tabs from '@radix-ui/react-tabs';
import * as DM from '@radix-ui/react-dropdown-menu';
import { ArrowDownToLine, ChevronDown, Copy, FilePlus2, FileSpreadsheet, FolderOpen, ListChecks, ListPlus, MoreHorizontal, PanelRight, RefreshCw, Save, Search, Trash2, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { api, download } from '@/lib/api';
import type { ListResult, ScreenDef } from '@/lib/types';
import type { GridColumn, LookupSpec } from '@/grid/types';
import { DataGrid } from '@/grid/DataGrid';
import { LookupDialog } from '@/grid/LookupDialog';
import { EditGrid, EditRow, isDirty, newKey, toEditRows } from '@/grid/EditGrid';
import { notifyDataChanged } from '@/grid/events';
import { dizaynColumns } from '../columns';
import { Spinner, ErrorBox } from '@/ui/Spinner';
import { confirmDialog, promptDialog } from '@/ui/confirm';
import { Dialog } from '@/ui/Dialog';
import { ReportMenu } from '@/reports/ReportMenu';
import { useTabId, useWorkspace } from '@/shell/workspace';
import { cn } from '@/lib/cn';
import { MasterForm } from './MasterForm';
import { KalemGirisi } from './KalemGirisi';
import { otomatikEvrakNo } from './calc';
import type { BelgeTanim, BelgeYukle, Row, SourceInfo } from './types';
import type { ColumnMeta } from '@/lib/types';

interface Doc {
  ver: number;
  master: Row;
  masterOriginal: Row | null;
  masterCols: ColumnMeta[];
  masterSource: SourceInfo | null;
  details: EditRow[];
  deleted: Row[];
  detailCols: ColumnMeta[];
  detailSource: SourceInfo | null;
  sag: { rows: EditRow[]; deleted: Row[]; cols: ColumnMeta[]; source: SourceInfo | null; linkId: number } | null;
  numId: number | null;
}

const up = (s: string) => s.toLocaleUpperCase('tr-TR');
const val = (r: Row | null | undefined, f: string) => (r ? r[Object.keys(r).find((k) => up(k) === up(f)) ?? f] : undefined);
const editableSet = (s: SourceInfo | null | undefined) => (s ? new Set(Object.keys(s.map).map(up)) : undefined);
const keyOf = (s: SourceInfo | null, r: Row) => {
  const ks = s?.key?.length ? s.key : ['ID'];
  const o: Row = {};
  for (const k of ks) o[k] = val(r, k);
  return o;
};

/**
 * Dizayn Master/Detail belge ekranı — Delphi Firin\Unit80_1 (Form80_1) ve türevleri (Form80_2/80_3/80_4, Form82).
 * Ana kayıt + detay satırları önbellekte düzenlenir, Kaydet (F5) hepsini tek veritabanı işleminde yazar:
 * HESAPLAMA → ana kayıt → detay (ANA_ID) → sağ panel → TABLE_POST / kayıt sonrası SP. Varyant farkları sunucudadır (VARYANTLAR).
 */
export function BelgeScreen({ screen, id }: { screen: ScreenDef; id?: number | null }) {
  const tag = screen.tag;
  const ws = useWorkspace();
  const tabId = useTabId();
  const tanimQ = useQuery({ queryKey: ['belge-tanim', tag], queryFn: () => api<BelgeTanim>(`/screens/${tag}/belge/tanim`) });
  const t = tanimQ.data;
  const [doc, setDoc] = useState<Doc | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [selDetail, setSelDetail] = useState<number | null>(null);
  const [selSag, setSelSag] = useState<number | null>(null);
  const [listOpen, setListOpen] = useState(false);
  const [kalemOpen, setKalemOpen] = useState<{ belgeId: number; cariId: number | null } | null>(null);
  const [numOpen, setNumOpen] = useState(false);
  const [stokAra, setStokAra] = useState<{ alan: 'STOK_KODU' | 'STOK_ADI'; metin: string } | null>(null);
  const [evrakNoOpen, setEvrakNoOpen] = useState(false);
  const [hedef, setHedef] = useState<'detail' | 'sag'>('detail');
  const [levelSel, setLevelSel] = useState<Row | null>(null);
  const beforeEditOk = useRef(new Set<string>());
  const rootRef = useRef<HTMLDivElement>(null);
  const r = t?.rights ?? screen.rights;
  const v = t?.varyant;

  // ------------------------------------------------------------------ yükleme
  const load = useCallback(async (docId: number) => {
    setBusy('yukle');
    try {
      const y = await api<BelgeYukle>(`/screens/${tag}/belge/yukle/${docId}`);
      beforeEditOk.current.clear();
      setDoc((d) => ({
        ver: (d?.ver ?? 0) + 1,
        master: y.master.row, masterOriginal: y.master.yeni ? null : { ...y.master.row }, masterCols: y.master.columns, masterSource: y.master.source,
        details: toEditRows(y.detail?.rows ?? []), deleted: [], detailCols: y.detail?.columns ?? [], detailSource: y.detail?.source ?? null,
        sag: y.sag ? { rows: toEditRows(y.sag.rows), deleted: [], cols: y.sag.columns, source: y.sag.source, linkId: y.sag.linkId } : null,
        numId: null,
      }));
      setSelDetail(y.detail?.rows.length ? 0 : null);
      setSelSag(null);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  }, [tag]);

  useEffect(() => {
    if (t?.tanimli) load(id ?? 0);
  }, [t?.tanimli, id, load]);

  const dirty = useMemo(() => {
    if (!doc) return false;
    const mDirty = !doc.masterOriginal || Object.keys(doc.master).some((k) => String(doc.master[k] ?? '') !== String(doc.masterOriginal?.[k] ?? ''));
    return (!!doc.masterOriginal && mDirty) || (!doc.masterOriginal && doc.details.length > 0) || doc.details.some(isDirty) || doc.deleted.length > 0
      || !!doc.sag && (doc.sag.rows.some(isDirty) || doc.sag.deleted.length > 0);
  }, [doc]);
  const docId = Number(val(doc?.master, 'ID') ?? 0) || 0;

  // ------------------------------------------------------------------ kaydet (BitBtn5Click)
  const save = useCallback(async (opts: { quiet?: boolean } = {}): Promise<number | null> => {
    if (!doc || !t) return null;
    if (docId > 0 ? !r.update : !r.insert) { toast.error(docId > 0 ? 'Düzeltme Yetkiniz Yok...' : 'Yeni kayıt yetkiniz yok'); return null; }
    setBusy('kaydet');
    try {
      const body = {
        master: doc.master, original: doc.masterOriginal, numId: doc.numId,
        detay: { rows: doc.details.map((x) => ({ row: x.row, original: x.original, key: x.original ? keyOf(doc.detailSource, x.original) : null })), deleted: doc.deleted },
        sag: doc.sag ? {
          rows: doc.sag.rows.filter(isDirty).map((x) => ({ row: x.row, original: x.original, key: x.original ? keyOf(doc.sag!.source, x.original) : null })),
          deleted: doc.sag.deleted, detailRow: selDetail != null ? doc.details[selDetail]?.row : null,
        } : undefined,
      };
      const res = await api<{ id: number }>(`/screens/${tag}/belge/kaydet`, { body });
      if (!opts.quiet) toast.success('Kaydedildi');
      notifyDataChanged(tag);
      await load(res.id);
      return res.id;
    } catch (e: any) {
      toast.error(e.message);
      return null;
    } finally {
      setBusy(null);
    }
  }, [doc, t, r, tag, load, selDetail, docId]);

  /** Değişiklik varsa sor: kaydet / vazgeç / iptal (Delphi Bitbtn6Click & FormClose) */
  const askSave = useCallback(async (): Promise<boolean> => {
    if (!dirty) return true;
    const yes = await confirmDialog('Değişiklikleri Kaydetmek İstiyormusunuz ', { ok: 'Kaydet', cancel: 'Kaydetme' });
    if (yes) return (await save()) != null;
    return true;
  }, [dirty, save]);

  useEffect(() => {
    ws.setGuard(tabId, dirty ? askSave : null);
    return () => ws.setGuard(tabId, null);
  }, [dirty, askSave, ws, tabId]);

  // ------------------------------------------------------------------ yeni / sil / liste / çoğalt
  const yeni = async () => {
    if (!r.insert) return;
    if (dirty && !(await confirmDialog('Kaydedilmemiş değişiklikler kaybolacak. Yeni belgeye geçilsin mi?', { ok: 'Yeni belge' }))) return;
    await load(0);
  };

  const sil = async () => {
    if (!docId || !r.delete) return;
    if (!(await confirmDialog('Silmek İstediğinizden Eminmisiniz ', { danger: true, ok: 'Sil' }))) return;
    if (t?.ayar.silmeOnay2 && !(await confirmDialog('Bu Kayda Baglı Detaylar Silinecektir Eminmisiniz  !', { danger: true, ok: 'Evet, sil' }))) return;
    setBusy('sil');
    try {
      await api(`/screens/${tag}/belge/sil`, { body: { master: doc!.master } });
      toast.success('Silindi');
      notifyDataChanged(tag);
      await load(0);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const listeden = async (picked: Row) => {
    const pid = Number(val(picked, 'ID') ?? Object.values(picked)[0]);
    if (!pid) return;
    if (dirty) {
      const yes = await confirmDialog('Değişiklikleri Kaydetmek İstiyormusunuz ', { ok: 'Kaydet', cancel: 'Kaydetme' });
      if (yes && (await save({ quiet: true })) == null) return;
    }
    await load(pid);
  };

  const cogalt = async () => {
    if (!t?.cogalt) return;
    const sid = await save({ quiet: true });
    if (!sid) return;
    setBusy('cogalt');
    try {
      const res = await api<{ newId: number | null; mesaj: string | null }>(`/screens/${tag}/belge/cogalt`, { body: { master: { ...doc!.master, ID: sid } } });
      if (res.mesaj) toast.success(res.mesaj);
      if (res.newId) await load(res.newId);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  // ------------------------------------------------------------------ kolonlar
  const masterColumns = useMemo<GridColumn[]>(() => (doc && t?.master ? dizaynColumns(doc.masterCols, t.master.fields, { tag, editableCols: editableSet(doc.masterSource) }) : []), [doc?.masterCols, doc?.masterSource, t, tag]);
  const stokLookup = useCallback((alan: 'STOK_KODU' | 'STOK_ADI'): LookupSpec => ({
    title: 'Stok Listesi',
    fetch: (q, row) => api<ListResult>(`/screens/${tag}/belge/stok-ara`, { body: { alan, metin: '', query: q, row } }),
    apply: (p) => ({ STOK_ID: p.ID }),
    validate: async (value) => {
      const res = await api<{ STOK_ID: unknown; mesaj?: string }>(`/screens/${tag}/belge/barkod`, { body: { metin: value } });
      return { ok: !res.mesaj, message: res.mesaj, values: { STOK_ID: res.STOK_ID } };
    },
  }), [tag]);
  const detailColumns = useMemo<GridColumn[]>(() => {
    if (!doc || !t?.detail) return [];
    return dizaynColumns(doc.detailCols, t.detail.fields, { tag, editableCols: editableSet(doc.detailSource) }).map((c) => {
      if (c.field?.stockPicker && !c.readonly) return { ...c, editor: 'lookup' as const, lookup: stokLookup(up(c.key) === 'STOK_ADI' ? 'STOK_ADI' : 'STOK_KODU') };
      return c;
    });
  }, [doc?.detailCols, doc?.detailSource, t, tag, stokLookup]);
  const sagColumns = useMemo<GridColumn[]>(() => (doc?.sag && t?.sag ? dizaynColumns(doc.sag.cols, t.sag.fields, { tag, editableCols: t.sag.editing ? editableSet(doc.sag.source) : new Set() }) : []), [doc?.sag?.cols, doc?.sag?.source, t, tag]);

  // ------------------------------------------------------------------ ana kayıt olayları
  const masterChange = async (patch: Row) => {
    setDoc((d) => (d ? { ...d, master: { ...d.master, ...patch } } : d));
  };
  const masterBeforeEdit = async (c: GridColumn): Promise<boolean> => {
    if (!v || v.masterBeforeEdit === 'yok' || !doc?.masterOriginal) return true;
    if (v.masterBeforeEdit === 'hepsi' && beforeEditOk.current.has('master')) return true;
    try {
      await api(`/screens/${tag}/belge/duzenleme-oncesi`, { body: { parca: 'master', master: doc.master, alan: c.key } });
      if (v.masterBeforeEdit === 'hepsi') beforeEditOk.current.add('master');
      return true;
    } catch (e: any) {
      toast.error(e.message);
      return false;
    }
  };

  // ------------------------------------------------------------------ detay olayları
  const validateFields = useMemo(() => new Set((t?.detail?.validateFields ?? []).slice(0, 5).map(up)), [t]);
  const detailChange = async (i: number, patch: Row) => {
    const cur = doc?.details[i];
    if (!cur) return;
    let row = { ...cur.row, ...patch };
    setDoc((d) => (d ? { ...d, details: d.details.map((x, k) => (k === i ? { ...x, row } : x)) } : d));
    // Fieldvalidate1..5 (ONVALIDATE): değişen alan doğrulama listesindeyse
    for (const f of Object.keys(patch)) {
      if (!validateFields.has(up(f))) continue;
      try {
        const res = await api<{ values: Row }>(`/screens/${tag}/belge/dogrula`, { body: { alan: f, master: doc!.master, row } });
        if (Object.keys(res.values).length) {
          row = { ...row, ...res.values };
          const rr = row;
          setDoc((d) => (d ? { ...d, details: d.details.map((x, k) => (k === i ? { ...x, row: rr } : x)) } : d));
        }
      } catch (e: any) {
        toast.error(e.message);
      }
    }
  };
  const detailLeave = async (i: number) => {
    if (!v?.detayHesaplama || !doc) return;
    const x = doc.details[i];
    if (!x) return;
    try {
      const res = await api<{ values: Row }>(`/screens/${tag}/belge/detay-hesapla`, { body: { master: doc.master, row: x.row } });
      if (Object.keys(res.values).length) setDoc((d) => (d ? { ...d, details: d.details.map((y) => (y._k === x._k ? { ...y, row: { ...y.row, ...res.values } } : y)) } : d));
    } catch (e: any) {
      toast.error(e.message);
    }
  };
  const detailBeforeEdit = async (i: number): Promise<boolean> => {
    if (!v?.detayBeforeEdit || !doc) return true;
    const x = doc.details[i];
    if (!x?.original || beforeEditOk.current.has(x._k)) return true;
    try {
      await api(`/screens/${tag}/belge/duzenleme-oncesi`, { body: { parca: 'detail', master: doc.master, row: x.row } });
      beforeEditOk.current.add(x._k);
      return true;
    } catch (e: any) {
      toast.error(e.message);
      return false;
    }
  };
  const satirKilitli = (i: number, tur: 'duzelt' | 'sil') => {
    const k = t?.ayar.satirKilidi;
    const x = doc?.details[i];
    if (!k || !x) return false;
    if (Number(val(x.row, k.alan) ?? 0) > 0) { toast.error(tur === 'sil' ? k.silMesaj : k.duzeltMesaj); return true; }
    return false;
  };
  const detailDelete = (i: number) => {
    if (!doc || satirKilitli(i, 'sil')) return;
    const x = doc.details[i];
    setDoc((d) => d ? { ...d, details: d.details.filter((_, k) => k !== i), deleted: x.original ? [...d.deleted, keyOf(d.detailSource, x.original)] : d.deleted } : d);
    setSelDetail((s) => (s == null ? null : Math.min(s, doc.details.length - 2) >= 0 ? Math.min(s, doc.details.length - 2) : null));
  };

  // sağ panel detay satırına bağlıysa (Dizayn.TAG=1) satır değişince yeniden okunur
  const sagDetay = t?.sag?.baglanti === 'detail';
  const selRow = selDetail != null ? doc?.details[selDetail] : undefined;
  useEffect(() => {
    if (!sagDetay || !doc) return;
    const did = Number(val(selRow?.row, 'ID') ?? 0);
    let stop = false;
    api<BelgeYukle['sag']>(`/screens/${tag}/belge/sag`, { body: { master: doc.master, detailRow: did > 0 ? selRow?.row : null } })
      .then((s) => { if (!stop) setDoc((d) => (d ? { ...d, sag: s ? { rows: toEditRows(s.rows), deleted: [], cols: s.columns, source: s.source, linkId: s.linkId } : null } : d)); })
      .catch(() => undefined);
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sagDetay, selRow?._k, doc?.ver]);

  // ------------------------------------------------------------------ aktarma (çift tık / Hepsini Aktar)
  const aktar = async (levelId: number, kaynak: Row[] | null) => {
    if (!doc || !r.insert && !r.update) return;
    if (hedef === 'sag' && !(Number(val(selRow?.row, 'ID') ?? 0) > 0)) { toast.info('Önce detay satırını kaydediniz'); return; }
    setBusy('aktar');
    try {
      const res = await api<{ rows: Row[]; atlanan: number }>(`/screens/${tag}/belge/aktar`, {
        body: { levelId, master: doc.master, mevcut: (hedef === 'sag' ? doc.sag?.rows ?? [] : doc.details).map((x) => x.row), kaynak: kaynak ?? undefined, hepsi: kaynak == null, hedef, detailRow: selRow?.row ?? null },
      });
      if (!res.rows.length) { toast.info(res.atlanan ? 'Seçilen kayıtlar zaten ekli' : 'Aktarılacak kayıt yok'); return; }
      const added: EditRow[] = res.rows.map((row) => ({ _k: newKey(), row, original: null }));
      if (hedef === 'sag') setDoc((d) => (d?.sag ? { ...d, sag: { ...d.sag, rows: [...d.sag.rows, ...added] } } : d));
      else {
        setDoc((d) => (d ? { ...d, details: [...d.details, ...added] } : d));
        setSelDetail((doc.details.length + added.length) - 1);
      }
      if (kaynak == null || res.rows.length > 1) toast.success(`${res.rows.length} satır aktarıldı${res.atlanan ? ` (${res.atlanan} tekrar atlandı)` : ''}`);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  // ------------------------------------------------------------------ kalem girişi (Form80_21/31/41)
  const kalemAc = async (i: number | null) => {
    if (!t?.ayar.kalemFormu || !doc) return;
    if (i != null && satirKilitli(i, 'duzelt')) return;
    if (docId > 0 && !r.update) { toast.error('Düzeltme Yetkiniz Yok...'); return; }
    const cari = i != null ? Number(val(doc.details[i]?.row, 'CARI_ID') ?? 0) || null : null;
    const sid = await save({ quiet: true }); // Delphi: bitbtn5.click
    if (!sid) return;
    setKalemOpen({ belgeId: sid, cariId: cari });
  };

  // ------------------------------------------------------------------ fırın varyant işlemleri
  const hepsineAyni = async () => {
    const s = await promptDialog('Evrak No', { title: 'Evrak No Giriniz', initial: '0', inputMode: 'numeric' });
    if (s == null) return;
    if (s === '0' || s === '') { toast.error('Evrak No Boş yada Sıfır Olamaz'); return; }
    setDoc((d) => (d ? { ...d, details: d.details.map((x) => ({ ...x, row: { ...x.row, [Object.keys(x.row).find((k) => up(k) === 'EVRAK_NO') ?? 'EVRAK_NO']: s } })) } : d));
  };
  const evrakNoUygula = async (ilk: number) => {
    setEvrakNoOpen(false);
    const tur = t!.ayar.otomatikEvrakNo!.tur;
    const hepsi = await confirmDialog('Tümünü Numaralandır...! ', { ok: 'Evet (tümü)', cancel: 'Hayır (seçili satırdan)' });
    const res = otomatikEvrakNo(doc!.details.map((x) => x.row), ilk, { tur, hepsi, baslangicIndex: selDetail ?? 0 });
    setDoc((d) => {
      if (!d) return d;
      const silKeys = new Set(res.silinen.map((i) => d.details[i]._k));
      const deleted = [...d.deleted, ...res.silinen.map((i) => d.details[i]).filter((x) => x.original).map((x) => keyOf(d.detailSource, x.original!))];
      const details = d.details.map((x, i) => (res.yeni[i] != null ? { ...x, row: { ...x.row, [Object.keys(x.row).find((k) => up(k) === 'EVRAK_NO') ?? 'EVRAK_NO']: res.yeni[i] } } : x)).filter((x) => !silKeys.has(x._k));
      return { ...d, details, deleted };
    });
    toast.success(`Evrak numaraları verildi${res.silinen.length ? ` (${res.silinen.length} boş satır silindi)` : ''}`);
  };
  const hesapKapat = async () => {
    const h = t?.ayar.hesapKapat;
    if (!h || !doc) return;
    const plasiyer = String(val(levelSel, h.alan) ?? '').trim();
    if (!val(doc.master, 'TARIH')) { toast.error(h.tarihMesaj ?? 'Lütfen bir tarih Giriniz...'); return; }
    if (!plasiyer) { toast.info('Listeden kasiyer seçiniz'); return; }
    if (!(await confirmDialog(`${plasiyer} kasiyerinin açık satışları kapatılacak. Devam edilsin mi?`, { ok: 'Hesap Kapat' }))) return;
    setBusy('kapat');
    try {
      const res = await api<{ id: number }>(`/screens/${tag}/belge/hesap-kapat`, { body: { master: doc.master, plasiyer } });
      notifyDataChanged(tag);
      if (res.id) {
        if (dirty && (await confirmDialog('Değişiklikleri Kaydetmek İstiyormusunuz ', { ok: 'Kaydet', cancel: 'Kaydetme' }))) await save({ quiet: true });
        await load(res.id);
      }
      toast.success('Hesap kapatıldı');
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };
  const excel = () => {
    if (!doc) return;
    const cols = detailColumns.filter((c) => !c.hidden).map((c) => ({ key: c.key, caption: c.caption, mask: c.mask, sqlType: c.sqlType }));
    download('/export/rows', { name: screen.title, columns: cols, rows: doc.details.map((x) => x.row) }, `${screen.title}.xlsx`).catch((e) => toast.error(e.message));
  };

  // ------------------------------------------------------------------ klavye (FormKeyDown)
  const onKeyDown = (e: React.KeyboardEvent) => {
    const k = e.key;
    const fk = /^F\d+$/.test(k);
    if (!fk) return;
    e.preventDefault();
    if (k === 'F2' && r.insert) yeni();
    if (k === 'F3' && r.delete) sil();
    if (k === 'F5') save();
    if (k === 'F6' && t?.master?.liste) setListOpen(true);
    if (k === 'F7') {
      if (t?.ayar.kalemFormu) kalemAc(selDetail);
      else if (hasStok) setStokAra({ alan: 'STOK_KODU', metin: '' });
    }
  };
  const hasStok = useMemo(() => !!doc?.detailCols.some((c) => up(c.name) === 'STOK_ID') && !!doc?.detailCols.some((c) => up(c.name) === 'STOK_KODU' || up(c.name) === 'STOK_ADI'), [doc?.detailCols]);

  // ------------------------------------------------------------------ çizim
  if (tanimQ.isLoading) return <Spinner />;
  if (tanimQ.error) return <ErrorBox error={tanimQ.error} />;
  if (!t?.tanimli) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="card max-w-md p-6 text-center">
          <div className="empty-ic mx-auto mb-3 grid h-12 w-12 place-items-center"><ListChecks size={22} /></div>
          <div className="card-title mb-1">Geçerli Dizayn Bulunamadı.</div>
          <p className="text-[12.5px] muted">Bu ekranın (<b>{screen.formTitle}</b>) Dizayn tanımı bu şirket veritabanında yok. Tanım Rapor/Dizayn yönetiminden eklendiğinde ekran kendiliğinden çalışır.</p>
        </div>
      </div>
    );
  }
  if (!doc) return <Spinner />;

  const reportExtra: Row = {};
  const selR = selRow?.row;
  if (val(doc.master, 'SERVIS_NO') != null) reportExtra['Servis No'] = val(doc.master, 'SERVIS_NO');
  if (v && (screen.settings?.varyant === 'Form80_2') && selR) { reportExtra['Cari Kodu'] = val(selR, 'CARI_KODU'); reportExtra['Cari Ünvanı'] = val(selR, 'CARI_UNVAN'); }
  if (selR && (screen.settings?.varyant === 'Form80_3' || screen.settings?.varyant === 'Form80_4')) {
    reportExtra['Baş Evrak No'] = val(selR, 'EVRAK_NO'); reportExtra['Baş. Evrak No'] = val(selR, 'EVRAK_NO');
    if (screen.settings?.varyant === 'Form80_4') reportExtra['Evrak No'] = val(selR, 'EVRAK_NO');
    reportExtra['Cari Kodu'] = val(selR, 'CARI_KODU'); reportExtra['Cari Ünvanı'] = val(selR, 'CARI_UNVAN');
  }
  const kalem = t.ayar.kalemFormu;
  const masterH = t.master?.height && t.master.height > 0 ? Math.max(96, t.master.height) : undefined;
  // Delphi Form80_2: cxGrid3.Left := Master.SOL_GENISLIK (detayın genişliği); yoksa Sag.YUKSEKLIK sağ panel genişliği
  const solW = t.master?.solGenislik && t.master.solGenislik > 0 ? t.master.solGenislik : null;
  const sagW = t.sag?.width && t.sag.width > 0 ? Math.min(Math.max(t.sag.width, 260), 720) : 420;

  return (
    <div ref={rootRef} className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 p-2 outline-none" tabIndex={-1} onKeyDown={onKeyDown}>
      {/* araç çubuğu */}
      <div className="panel flex flex-wrap items-center gap-1.5 rounded px-2 py-1.5">
        <button className="btn" disabled={!r.insert || !!busy} onClick={yeni} title="Yeni (F2)"><FilePlus2 size={14} /> Yeni</button>
        <button className="btn btn-primary" disabled={!!busy || (docId > 0 ? !r.update : !r.insert)} onClick={() => save()} title="Kaydet (F5)"><Save size={14} /> {busy === 'kaydet' ? 'Kaydediliyor…' : 'Kaydet'}</button>
        <button className="btn btn-danger" disabled={!docId || !r.delete || !!busy} onClick={sil} title="Sil (F3)"><Trash2 size={14} /> Sil</button>
        {t.master?.liste && <button className="btn" disabled={!!busy} onClick={() => setListOpen(true)} title="Liste (F6)"><FolderOpen size={14} /> Liste</button>}
        {t.cogalt && <button className="btn" disabled={!docId || !r.insert || !!busy} onClick={cogalt}><Copy size={14} /> Çoğalt</button>}
        {kalem && <button className="btn" disabled={!!busy || !doc.details.length} onClick={() => kalemAc(selDetail)} title="Yeni Fiş Girişi (F7)"><ListPlus size={14} /> {kalem.tur === 'servis' ? 'Fiş Girişi' : 'Kalem Girişi'}</button>}
        {!kalem && hasStok && <button className="btn" disabled={selDetail == null} onClick={() => setStokAra({ alan: 'STOK_KODU', metin: '' })} title="Stok ara (F7)"><Search size={14} /> Stok Ara</button>}
        {t.ayar.hesapKapat && <button className="btn" disabled={!!busy || !r.insert} onClick={hesapKapat} title="Hesap Kapat (Ctrl+H)"><Wallet size={14} /> Hesap Kapat</button>}
        {(t.ayar.otomatikEvrakNo || t.ayar.hepsineAyniEvrakNo) && (
          <DM.Root>
            <DM.Trigger className="btn" disabled={!doc.details.length}>Evrak No <ChevronDown size={13} /></DM.Trigger>
            <DM.Portal>
              <DM.Content align="start" className="menu z-50 min-w-52 p-1">
                {t.ayar.otomatikEvrakNo && <DM.Item className="menu-item" onSelect={() => setEvrakNoOpen(true)}>Otomatik Evrak No</DM.Item>}
                {t.ayar.hepsineAyniEvrakNo && <DM.Item className="menu-item" onSelect={hepsineAyni}>Hepsine Aynı Evrak No</DM.Item>}
              </DM.Content>
            </DM.Portal>
          </DM.Root>
        )}
        <button className="btn" onClick={excel} disabled={!doc.details.length} title="Excel"><FileSpreadsheet size={14} /> Excel</button>
        <span className="ml-1 text-[11.5px] muted">
          {docId ? <>Kayıt No <b className="num text-[var(--text)]">{docId}</b></> : <span className="badge chip-accent">Yeni belge</span>}
          {dirty && <span className="badge ml-2 bg-[var(--warning-soft)] text-[var(--warning-text)]">kaydedilmedi</span>}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          {busy && <span className="text-[11px] muted">işleniyor…</span>}
          <ReportMenu reports={t.reports} row={docId ? doc.master : null} params={reportExtra}
            beforeOpen={t.ayar.raporOncesiKaydet ? async () => (await save({ quiet: true })) != null : undefined} />
        </div>
      </div>

      {/* ana kayıt */}
      <div className="card shrink-0 overflow-auto p-3 scroll-thin" style={{ maxHeight: masterH ? masterH + 40 : '34vh' }}>
        <MasterForm key={doc.ver} columns={masterColumns} row={doc.master} readOnly={docId > 0 ? !r.update : !r.insert}
          onChange={masterChange} onBeforeEdit={masterBeforeEdit}
          onNumarator={v?.numarator ? () => setNumOpen(true) : undefined} />
      </div>

      {/* detay + sağ */}
      <div className="flex min-h-0 flex-[3] gap-2">
        {t.detail ? (
          <EditGrid className={solW ? 'min-w-0 shrink' : 'min-w-0 flex-1'} style={solW ? { flex: `0 1 ${solW}px` } : undefined} title="Satırlar" columns={detailColumns} rows={doc.details} selected={selDetail} onSelect={setSelDetail}
            layoutKey={`belge:${screen.formTitle}:Detail`}
            readOnly={docId > 0 ? !r.update : !r.insert}
            onChange={detailChange} onRowLeave={detailLeave} beforeEdit={(i) => detailBeforeEdit(i)}
            onRowDoubleClick={kalem ? (i) => kalemAc(i) : undefined}
            onDelete={detailDelete}
            emptyText={t.levels.length ? 'Satır yok — alttaki listeden çift tıklayarak ya da "Hepsini Aktar" ile ekleyin' : 'Satır yok'} />
        ) : <div className="panel flex flex-1 items-center justify-center rounded muted">Detay tanımı yok</div>}
        {doc.sag && t.sag && (
          <EditGrid className={solW ? 'min-w-[260px] flex-1' : 'shrink-0'} style={solW ? undefined : { width: sagW }} title={<span className="flex items-center gap-1.5"><PanelRight size={14} /> {t.sag.baglanti === 'detail' ? 'Satır ayrıntısı' : 'Özet'}</span>}
            columns={sagColumns} rows={doc.sag.rows} selected={selSag} onSelect={setSelSag}
            layoutKey={`belge:${screen.formTitle}:Sag`} readOnly={!t.sag.editing}
            onChange={t.sag.editing ? (i, p) => setDoc((d) => (d?.sag ? { ...d, sag: { ...d.sag, rows: d.sag.rows.map((x, k) => (k === i ? { ...x, row: { ...x.row, ...p } } : x)) } } : d)) : undefined}
            onRowLeave={t.sag.editing && t.sag.hesaplama ? async (i) => {
              const x = doc.sag!.rows[i];
              try {
                const res = await api<{ values: Row }>(`/screens/${tag}/belge/sag-hesapla`, { body: { detailRow: selRow?.row ?? {}, row: x.row } });
                setDoc((d) => (d?.sag ? { ...d, sag: { ...d.sag, rows: d.sag.rows.map((y) => (y._k === x._k ? { ...y, row: { ...y.row, ...res.values } } : y)) } } : d));
              } catch (e: any) { toast.error(e.message); }
            } : undefined}
            onDelete={t.sag.editing ? (i) => setDoc((d) => (d?.sag ? { ...d, sag: { ...d.sag, rows: d.sag.rows.filter((_, k) => k !== i), deleted: d.sag.rows[i].original ? [...d.sag.deleted, keyOf(d.sag.source, d.sag.rows[i].original!)] : d.sag.deleted } } : d)) : undefined}
            emptyText="Kayıt yok" footer />
        )}
      </div>

      {/* liste seviyeleri (cxGrid1 Level1..20) */}
      {t.levels.length > 0 && (
        <LevelsPanel tag={tag} t={t} master={doc.master} canTransfer={docId > 0 ? r.update : r.insert}
          onTransfer={aktar} onSelect={setLevelSel}
          hedef={t.sag?.editing ? hedef : null} onHedef={setHedef} formTitle={screen.formTitle} />
      )}

      {listOpen && (
        <LookupDialog open={listOpen} onClose={() => setListOpen(false)} row={{}}
          spec={{ title: `${screen.formTitle} Listesi`, fetch: (q) => api(`/screens/${tag}/belge/liste`, { body: { query: q } }), apply: (p) => p }}
          onPick={(p) => listeden(p)} />
      )}
      {stokAra && (
        <LookupDialog open onClose={() => setStokAra(null)} row={{}}
          spec={{ title: 'Stok Listesi', fetch: (q) => api(`/screens/${tag}/belge/stok-ara`, { body: { alan: stokAra.alan, metin: stokAra.metin, query: q } }), apply: (p) => p }}
          onPick={(p) => { if (selDetail != null) detailChange(selDetail, { STOK_ID: p.ID }); }} />
      )}
      {numOpen && <NumaratorDialog tag={tag} onClose={() => setNumOpen(false)} onPick={(evrakNo, numId) => { setDoc((d) => (d ? { ...d, master: { ...d.master, [Object.keys(d.master).find((k) => up(k) === 'EVRAK_NO') ?? 'EVRAK_NO']: evrakNo }, numId } : d)); setNumOpen(false); }} />}
      {evrakNoOpen && t.ayar.otomatikEvrakNo && <OtomatikEvrakNoDialog tag={tag} tur={t.ayar.otomatikEvrakNo.tur} onClose={() => setEvrakNoOpen(false)} onOk={evrakNoUygula} />}
      {kalemOpen && kalem && (
        <KalemGirisi tag={tag} belgeId={kalemOpen.belgeId} cariId={kalemOpen.cariId} baslik={`${screen.formTitle} · ${kalem.grup}`}
          onClose={() => { setKalemOpen(null); load(kalemOpen.belgeId); }} />
      )}
    </div>
  );
}

/** Alt liste seviyeleri: DIZAYN_ADI Level1..N, MST_ parametreleri ana kayıttan (LISTE_PARAMETRE_AL) */
function LevelsPanel({ tag, t, master, canTransfer, onTransfer, onSelect, hedef, onHedef, formTitle }: {
  tag: number; t: BelgeTanim; master: Row; canTransfer: boolean; onTransfer: (levelId: number, rows: Row[] | null) => void;
  onSelect: (r: Row | null) => void; hedef: 'detail' | 'sag' | null; onHedef: (h: 'detail' | 'sag') => void; formTitle: string;
}) {
  const [active, setActive] = useState(String(t.levels[0].id));
  const [sel, setSel] = useState<Row | null>(null);
  const [reload, setReload] = useState(0);
  const lv = t.levels.find((x) => String(x.id) === active) ?? t.levels[0];
  const mstVals = lv.mstParams.map((p) => String(val(master, p) ?? ''));
  const h = t.levelsHeight && t.levelsHeight > 0 ? `${Math.max(160, t.levelsHeight)}px` : undefined;
  const cols = useCallback((meta: ColumnMeta[]) => dizaynColumns(meta, lv.fields, { tag, editableCols: new Set() }), [lv, tag]);
  return (
    <Tabs.Root value={active} onValueChange={(x) => { setActive(x); setSel(null); onSelect(null); }} className="flex min-h-0 flex-[2] flex-col" style={h ? { flex: `0 0 ${h}` } : undefined}>
      <div className="flex flex-wrap items-center gap-1">
        <Tabs.List className="flex gap-0.5">
          {t.levels.map((x) => (
            <Tabs.Trigger key={x.id} value={String(x.id)} className="tab px-3">{x.caption}</Tabs.Trigger>
          ))}
        </Tabs.List>
        {hedef && (
          <div className="seg ml-2 flex p-0.5 text-[11.5px]">
            <button className={cn('seg-item px-2 py-0.5', hedef === 'detail' && 'is-on')} onClick={() => onHedef('detail')}>Detaya aktar</button>
            <button className={cn('seg-item px-2 py-0.5', hedef === 'sag' && 'is-on')} onClick={() => onHedef('sag')}>Sağa aktar</button>
          </div>
        )}
        <span className="ml-2 text-[11px] muted">Çift tıklayarak aktarın</span>
      </div>
      <DataGrid key={`${lv.id}`}
        queryKey={['belge-seviye', tag, lv.id, ...mstVals, reload]}
        fetch={(q) => api(`/screens/${tag}/belge/seviye/${lv.id}`, { body: { master, query: q } })}
        buildColumns={cols}
        layoutKey={`belge:${formTitle}:${lv.name}`}
        onSelectionChange={(r) => { setSel(r); onSelect(r); }}
        onRowOpen={(r) => canTransfer && onTransfer(lv.id, [r])}
        toolbarLeft={<>
          <button className="btn" disabled={!sel || !canTransfer} onClick={() => sel && onTransfer(lv.id, [sel])}><ArrowDownToLine size={14} /> Aktar</button>
          <button className="btn" disabled={!canTransfer} onClick={() => onTransfer(lv.id, null)}><ListPlus size={14} /> Hepsini Aktar</button>
          <button className="btn btn-ghost" onClick={() => setReload((n) => n + 1)} title="Güncelle (F8)"><RefreshCw size={14} /></button>
        </>}
      />
    </Tabs.Root>
  );
}

/** Form908: numaratör seçimi (Bls_Numarator.TAG → EVRAK_CINSI) */
function NumaratorDialog({ tag, onClose, onPick }: { tag: number; onClose: () => void; onPick: (evrakNo: string, numId: number) => void }) {
  const q = useQuery({ queryKey: ['belge-num', tag], queryFn: () => api<{ evrakCinsi: string; rows: Row[] }>(`/screens/${tag}/belge/numarator`), retry: false });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Numaratör Seçimi" className="w-[min(640px,94vw)]">
      {q.isLoading ? <Spinner /> : q.error ? <ErrorBox error={q.error} /> : (
        <table className="dash-table w-full text-[12.5px]">
          <thead><tr><th>Ön Takı</th><th>Son Takı</th><th>Numara</th><th>Hane</th><th>Açıklama</th><th>Örnek</th><th /></tr></thead>
          <tbody>
            {q.data!.rows.map((x) => (
              <tr key={x.ID} className="cursor-pointer" onDoubleClick={() => onPick(x.EVRAK_NO_ORNEK, x.ID)}>
                <td>{x.ON_TAKI}</td><td>{x.SON_TAKI}</td><td className="num">{x.NUMARATOR}</td><td className="num">{x.HANE}</td><td>{x.ACIKLAMA}</td>
                <td className="num font-semibold">{x.EVRAK_NO_ORNEK}</td>
                <td><button className="btn btn-primary" onClick={() => onPick(x.EVRAK_NO_ORNEK, x.ID)}>Seç</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Dialog>
  );
}

/** Form80_32: başlangıç evrak no (Son_Fatura_No / Son_Irsaliye_No önerisi, F6 liste) */
function OtomatikEvrakNoDialog({ tag, tur, onClose, onOk }: { tag: number; tur: string; onClose: () => void; onOk: (ilk: number) => void }) {
  const q = useQuery({ queryKey: ['belge-sonevrak', tag], queryFn: () => api<{ rows: Row[]; columns: ColumnMeta[]; oneri: string | null }>(`/screens/${tag}/belge/son-evrak-no`), retry: false });
  const [no, setNo] = useState<string>('');
  const [liste, setListe] = useState(false);
  useEffect(() => { if (q.data?.oneri != null && no === '') setNo(String(q.data.oneri).trim()); }, [q.data]);
  const ok = () => {
    const n = Number(no);
    if (!Number.isInteger(n)) { toast.error('Evrak No Sayısal Değer Değildir. Değiştiriniz...!'); return; }
    onOk(n);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Otomatik Evrak No" className="w-[min(460px,92vw)]"
      footer={<><button className="btn" onClick={onClose}>Çıkış</button><button className="btn btn-primary" onClick={ok}>Tamam</button></>}>
      <div className="flex flex-col gap-3">
        <div className="badge chip-accent w-fit">{tur}</div>
        <label className="flex flex-col gap-1">
          <span className="h-label">Başlangıç Evrak No Giriniz</span>
          <div className="flex gap-1.5">
            <input data-autofocus className="inp flex-1 num" inputMode="numeric" value={no} onChange={(e) => setNo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') ok(); if (e.key === 'F6') { e.preventDefault(); setListe(true); } }} />
            <button className="btn" onClick={() => setListe(true)} title="Liste (F6)"><MoreHorizontal size={14} /></button>
          </div>
        </label>
        {liste && q.data && (
          <div className="max-h-60 overflow-auto rounded border b-border scroll-thin">
            <table className="dash-table w-full text-[12.5px]">
              <thead><tr>{q.data.columns.map((c) => <th key={c.name}>{c.name}</th>)}</tr></thead>
              <tbody>{q.data.rows.map((x, i) => (
                <tr key={i} className="cursor-pointer" onClick={() => { setNo(String(x.EVRAK_NO ?? '').trim()); setListe(false); }}>
                  {q.data!.columns.map((c) => <td key={c.name}>{String(x[c.name] ?? '')}</td>)}
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </Dialog>
  );
}
